import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Button, DateRangePicker, Info, PageHeader, Table } from '@demandlocal/components'
import LineChart from '../../../../components/Charts/LineChart'
import { ReactComponent as DownloadIcon } from '../../../../images/icons/action_download.svg'
import { formatNumber } from '../../../../lib/utils/formatNumber'
import { createColumnHelper } from '@tanstack/react-table'
import PolarAreaChart from '../../../../components/Charts/PolarAreaChart'
import { useSearchParams } from 'react-router-dom'
import { useDownloadPDFReport } from '../../../../api/dealership/reports/useDownloadPDFReport'
import toast from 'react-hot-toast'
import { DateRange } from '../../../../lib/types/DateRange'
import { GA4 } from '../../../../lib/utils/ga4'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import mtm from '../../../../lib/utils/mtm'
import { useDateRange } from '../../../../store/zustand/dateRangeStore'
import { useGetVerifiedSalesReport } from '../../../../api/dealership/inventory/useGetVerifiedSalesReport'
import { lowerCase, startCase, take } from 'lodash'
import { useGetPolkCompetitorSales } from '../../../../api/dealership/inventory/useGetPolkCompetitorSales'

const columnHelper = createColumnHelper<{
  dealer_display_name: string
  dealer_street: string
  dealer_city: string
  new: number
  used: number
  sales: number
  attributed_sales: number
}>()

const VerfiedSalesTemplate = ({
  children,
  filters,
  setFilters,
}: PropsWithChildren<{
  filters: DateRange
  setFilters: Dispatch<SetStateAction<DateRange>>
}>): JSX.Element => {
  const dealership = useDealership()
  const { setDateRange } = useDateRange()

  const { data, isLoading: isLoadingReport } = useGetVerifiedSalesReport({
    filters: {
      start_date: filters?.dateRange?.startDate,
      end_date: filters?.dateRange?.endDate,
    },
  })

  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  useEffect(() => {
    if (!isLoadingReport) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoadingReport])

  return (
    <div className={'flex flex-1 flex-col'} id={'content'}>
      <PageHeader
        title={`${dealership?.dealership} - Verified Sales`}
        cardsOptions={[
          {
            title: 'Dealers in group',
            content: formatNumber(data?.polkDealers?.length || 0),
            color: 'bg-palatinate-blue/40',
          },
          {
            title: 'Total sales',
            content: formatNumber(
              data?.totalSales?.reduce((acc, cur) => acc + Number(cur.sales), 0) || 0,
            ),
            color: 'bg-palatinate-blue/20',
            info: 'Total reported sales for the dealer group',
          },
          {
            title: 'Attributed sales',
            content: formatNumber(
              data?.attributedSalesByDate?.reduce((acc, cur) => acc + cur.sales, 0) || 0,
            ),
            color: 'bg-kelly-green/30',
            info: 'Vehicles sold to people who were exposed to and interacted with an ad',
          },
        ]}
        rightSlot={({ hidden }) => (
          <div className={'print:hidden flex flex-row gap-2'}>
            <DateRangePicker
              collapsed={hidden}
              filters={filters}
              onChange={onDateRangeChange}
            />
            <Button
              disabled={isDownloadingReport}
              collapsed={hidden}
              leftSlot={<DownloadIcon className={'fill-white'} />}
              onClick={async (e) => {
                e.preventDefault()
                const toastId = toast.loading('Generating report...')
                GA4.sendEvent({
                  event_name: 'export_pdf',
                  event_params: {
                    report_type: 'Polk',
                  },
                })
                mtm.sendEvent('Export', 'PDF', 'Polk')
                await downloadReport(undefined, {
                  async onSuccess(data) {
                    toast.success('Report generated successfully', { id: toastId })
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `Verified-Sales_${filters?.dateRange?.startDate}_-_${filters?.dateRange?.endDate}.pdf`
                    document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                    a.click()
                    a.remove()
                  },
                })
              }}>
              Export as PDF
            </Button>
          </div>
        )}
      />
      <div className={'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full'}>
        <div className={'mt-12 mb-5'} />
        {children}
      </div>
    </div>
  )
}

const VerfiedSalesReport = (): JSX.Element => {
  const [queryParams, setQueryParams] = useSearchParams()
  const { dateRange } = useDateRange()

  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })

  const {
    data,
    isLoading: isLoadingReport,
    isPreviousData: isPreviousReportsData,
  } = useGetVerifiedSalesReport({
    filters: {
      start_date: filters?.dateRange?.startDate,
      end_date: filters?.dateRange?.endDate,
    },
  })

  useEffect(() => {
    setQueryParams(filters.dateRange)
  }, [filters, setQueryParams])

  const competitorColumns = useMemo(() => {
    return [
      columnHelper.accessor('dealer_display_name', {
        header: 'Dealership',
        cell: (info) => startCase(lowerCase(info.getValue())),
      }),
      columnHelper.accessor('dealer_street', {
        header: 'Address',
        cell: (info) => startCase(lowerCase(info.getValue())),
      }),
      columnHelper.accessor('dealer_city', {
        header: 'City',
        cell: (info) => startCase(lowerCase(info.getValue())),
      }),
      columnHelper.accessor('sales', {
        header: 'Sales',
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
      columnHelper.accessor('attributed_sales', {
        header: () => {
          return (
            <div className={'flex flex-row gap-2 items-center justify-between'}>
              <p>Lost sales</p>
              <Info
                message={
                  'Number of people who saw your ads but purchased at a different dealership'
                }
              />
            </div>
          )
        },
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
    ]
  }, [])

  return (
    <VerfiedSalesTemplate filters={filters} setFilters={setFilters}>
      <div className={'flex flex-col gap-8'}>
        <div className={'flex grid grid-cols-1 gap-5'}>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <div className="flex flex-row gap-2 items-center">
                <span>Attributed sales</span>
                <Info message={'Data shown weekly'} />
              </div>
              <span>
                {!isLoadingReport &&
                  new Intl.NumberFormat('en-US').format(
                    data?.attributedSalesByDate?.reduce(
                      (acc, cur) => acc + cur.sales,
                      0,
                    ) || 0,
                  )}
              </span>
            </div>
            <div className={'h-[270px] p-2'}>
              <LineChart
                data={data?.attributedSalesByDate || []}
                gradient={'green-to-cyan'}
                yKey={'sales'}
                isLoading={isLoadingReport}
                isPreviousData={isPreviousReportsData}
              />
            </div>
          </div>
        </div>
        <div className={'flex grid grid-cols-3 gap-3'}>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>Sales by vehicle state</span>
            </div>
            <div className={'h-[270px] p-2'}>
              <PolarAreaChart
                data={
                  take(
                    data?.salesByState.map((d) => ({ label: d.state, value: d.sales })),
                    5,
                  ) || []
                }
              />
            </div>
          </div>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>Sales by make</span>
            </div>
            <div className={'h-[270px] p-2'}>
              <PolarAreaChart
                data={
                  take(
                    data?.salesByMake.map((d) => ({ label: d.make, value: d.sales })),
                    5,
                  ) || []
                }
              />
            </div>
          </div>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>Sales by model</span>
            </div>
            <div className={'h-[270px] p-2'}>
              <PolarAreaChart
                data={
                  take(
                    data?.salesByModel.map((d) => ({ label: d.model, value: d.sales })),
                    5,
                  ) || []
                }
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <h2 className="font-bold">Dealers in group</h2>
          <div className="grid grid-cols-4 gap-2">
            {data?.salesByDealer?.map((dealer) => {
              return (
                <div key={dealer.polk_dealer_id} className="border-2 rounded-xl p-3">
                  <div className="flex flex-col gap-2 justify-between h-full">
                    <div className="flex flex-col gap-2">
                      <h3 className="font-bold text-sm">{dealer.dealer_display_name}</h3>
                      <p className="text-xs">
                        {dealer.dealer_street}, {dealer.dealer_city}
                      </p>
                    </div>
                    <div className="w-full grid grid-cols-2 gap-2 divide-x border-t-2">
                      <div className="flex flex-col gap-2 pt-2">
                        <h5 className="text-xs font-bold">Sales</h5>
                        <p className="text-xs">{formatNumber(dealer.sales || 0)}</p>
                      </div>
                      <div className="flex flex-col gap-2 px-2 pt-2">
                        <h5 className="text-xs font-bold">Attributed sales</h5>
                        <p className="text-xs">
                          {formatNumber(dealer.attributed_sales || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        <div className={'flex flex-col gap-4'}>
          <span className={'font-bold text-primary-xiketic text-sm'}>
            Competitor sales
          </span>
          <Table
            emptyLabel={'No data found'}
            columns={competitorColumns}
            filters={{
              start_date: filters?.dateRange?.startDate,
              end_date: filters?.dateRange?.endDate,
            }}
            defaultSorting={[
              {
                id: 'sales',
                desc: true,
              },
            ]}
            fetchFn={useGetPolkCompetitorSales}
            isDownloadable={true}
          />
        </div>
      </div>
    </VerfiedSalesTemplate>
  )
}

export default VerfiedSalesReport
